import classNames from 'classnames';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useElementSize, useDebouncedCallback } from '@mantine/hooks';
import { useTheme } from '@mui/material/styles';
import {
  ClickAwayListener,
  Paper,
  Popper,
  ToggleButtonGroup,
  Toolbar,
  Box,
} from '@mui/material';
import { EditorEvents } from '@tiptap/core';
import { useTranslation } from 'react-i18next';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import InsertPageBreakIcon from '@mui/icons-material/InsertPageBreak';
import RedoIcon from '@mui/icons-material/Redo';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PrintDisabledIcon from '@mui/icons-material/PrintDisabled';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import EditorBubbleMenu from 'components/EditorBubbleMenu/EditorBubbleMenu';
import { useEditorBubbleMenu } from 'components/EditorBubbleMenu/context';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { useAppSettings } from 'providers/contexts/SettingsContext';
import AISelect from '../RichTextInput/AISelect/AISelect';
import HeadingSelect from '../RichTextInput/HeadingSelect/HeadingSelect';
import LinkSelect from '../RichTextInput/LinkSelect/LinkSelect';
import SpecialCharacters from '../RichTextInput/SpecialCharacters/SpecialCharacters';
import TextAlignSelect from '../RichTextInput/TextAlignSelect/TextAlignSelect';
import { WidgetSelect } from '../RichTextInput/WidgetSelect/WidgetSelect';
import EditorBarButtons from './components/EditorBarButtons';
import { hasExtension } from './utils';
import { TToolbarButton, TToolbarProps } from './types';
import { ARTICLE_MAIN_EDITOR_TOOLBAR_ID, breakPoints } from './const';
import { useToolbarActions } from './hooks';
import SearchAndReplaceMenu from 'components/SearchAndReplaceMenu/SearchAndReplaceMenu';
import { hasPrintOptions } from 'screens/editorial/asset/components/common/Print';

/**
 * Notes:
 * - Do NOT memoize this component--it creates a disconnect with the TipTap editor.
 * - Changes here impact the services editor (/services/edit/<service id>).
 */
const EditorBar = ({
  articleMainContent = false,
  buttons = [],
  editor,
  id,
  disabled,
  isMobileMenuEnabled = false,
  sx,
  setBubbleView: setBubbleViewProp,
}: TToolbarProps) => {
  const { t } = useTranslation();
  const {
    appSettings: { site: siteSettings },
  } = useAppSettings();
  const { sessionConfig } = useCosmos();
  const serviceKeys = sessionConfig?.service_keys;
  const hasPrint = hasPrintOptions(sessionConfig);
  const theme = useTheme();
  const isAllButtons = Array.isArray(buttons) ? false : buttons === 'all';
  const isArticleMainEditor = id === ARTICLE_MAIN_EDITOR_TOOLBAR_ID;

  // Get setBubbleView from context, otherwise fall back to prop
  let contextSetBubbleView;
  try {
    const context = useEditorBubbleMenu();
    contextSetBubbleView = context.setBubbleView;
  } catch {
    // Not wrapped in provider, use prop
    contextSetBubbleView = undefined;
  }
  const setBubbleView = contextSetBubbleView || setBubbleViewProp;

  //States
  const [isUndoEnabled, setIsUndoEnabled] = useState(false);
  const [isRedoEnabled, setIsRedoEnabled] = useState(false);
  const [isSelectionEmpty, setIsSelectionEmpty] = useState(false);
  const [searchReplaceOpen, setSearchReplaceOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // Active format states
  const [isActiveBold, setIsActiveBold] = useState(false);
  const [isActiveItalic, setIsActiveItalic] = useState(false);
  const [isActiveUnderline, setIsActiveUnderline] = useState(false);
  const [isActiveLink, setIsActiveLink] = useState(false);
  const [isActiveBulletList, setIsActiveBulletList] = useState(false);
  const [isActiveOrderedList, setIsActiveOrderedList] = useState(false);
  const [isActiveBlockQuote, setIsActiveBlockQuote] = useState(false);
  const [isActiveInlineNote, setIsActiveInlineNote] = useState(false);
  const [isActivePrintTrim, setIsActivePrintTrim] = useState(false);

  const { ref, width: elementWidth } = useElementSize();
  const isAIEnabled =
    siteSettings?.editorial?.content_extensions &&
    Boolean(serviceKeys?.tiptap_ai_app && serviceKeys?.tiptap_ai_token);
  const popperId = 'editor-toolbar-popper';

  // Sync the active format states with the editor state on transaction update
  const syncActiveFormatStates = useCallback((currentEditor: TToolbarProps['editor']) => {
    setIsActiveBold(currentEditor?.isActive('bold') ?? false);
    setIsActiveItalic(currentEditor?.isActive('italic') ?? false);
    setIsActiveUnderline(currentEditor?.isActive('underline') ?? false);
    setIsActiveLink(currentEditor?.isActive('link') ?? false);
    setIsActiveBulletList(currentEditor?.isActive('bulletList') ?? false);
    setIsActiveOrderedList(currentEditor?.isActive('orderedList') ?? false);
    setIsActiveBlockQuote(currentEditor?.isActive('blockquote') ?? false);
    setIsActiveInlineNote(currentEditor?.isActive('inlineNote') ?? false);
    setIsActivePrintTrim(currentEditor?.isActive('printTrim') ?? false);
  }, []);

  // Listen for Tiptap transaction updates
  const onEditorTxnHandler = useCallback(
    ({ editor }: EditorEvents['update']) => {
      if (!editor || editor?.isDestroyed) {
        return;
      }
      try {
        setIsUndoEnabled(Boolean(editor?.can?.()?.undo?.()));
        setIsRedoEnabled(Boolean(editor?.can?.()?.redo?.()));
      } catch (e) {
        setIsUndoEnabled(false);
        setIsRedoEnabled(false);
      }
      setIsSelectionEmpty(Boolean(editor?.view?.state?.selection?.empty));
      syncActiveFormatStates(editor);
    },
    [syncActiveFormatStates],
  );

  const debouncedTxnHandler = useDebouncedCallback(onEditorTxnHandler, {
    delay: 30,
    flushOnUnmount: true,
  });

  useEffect(() => {
    if (!editor || editor?.isDestroyed) return;
    // Set initial states (guard editor.can() which may throw if editor is torn down)
    try {
      setIsUndoEnabled(Boolean(editor?.can?.()?.undo?.()));
      setIsRedoEnabled(Boolean(editor?.can?.()?.redo?.()));
    } catch (e) {
      setIsUndoEnabled(false);
      setIsRedoEnabled(false);
    }
    setIsSelectionEmpty(Boolean(editor?.view?.state?.selection?.empty));
    syncActiveFormatStates(editor);
    editor.on('transaction', debouncedTxnHandler);
    // The selection has changed.
    editor?.on('selectionUpdate', ({ editor }) => {
      if (!editor || editor?.isDestroyed) return;
      syncActiveFormatStates(editor);
    });
    return () => {
      editor.off('transaction', debouncedTxnHandler);
      editor?.off('selectionUpdate');
    };
  }, [editor, debouncedTxnHandler, syncActiveFormatStates]);

  // Get toolbar actions
  const actions = useToolbarActions(editor, setBubbleView);

  const hasHeadingExt = hasExtension(editor, 'heading');
  const hasBoldExt = hasExtension(editor, 'bold');
  const hasItalicExt = hasExtension(editor, 'italic');
  const hasUnderlineExt = hasExtension(editor, 'underline');
  const hasLinkExt = hasExtension(editor, 'link') && hasExtension(editor, 'bubbleMenu');
  const hasTextAlignExt = hasExtension(editor, 'textAlign');
  const hasBulletListExt = hasExtension(editor, 'bulletList');
  const hasOrderedListExt = hasExtension(editor, 'orderedList');
  const hasBlockQuoteExt = hasExtension(editor, 'blockquote');
  const hasInlineNoteExt = hasExtension(editor, 'inlineNote');
  const hasPrintTrimExt = hasExtension(editor, 'printTrim');
  const hasTableExt = hasExtension(editor, 'table');
  const hasAiSelectExt = hasExtension(editor, 'ai');
  const hasSearchReplaceExt = hasExtension(editor, 'searchAndReplace');

  // Need to disambiguate tncms pageBreak hr and basic horizontalRule.
  // Note: article tagline field, hr seems valid, but not tncms break.
  // Since both are under the same extension name 'horizontalRule',
  // but body copy field loads the HorizontalRuleMulti extension
  // which has a custom command setPageBreak, we need to
  // check the individual editor.can commands, as other fields
  // might be using the base starterKit ext under the same name.
  const canInsertHorizRule = (() => {
    if (!editor || editor?.isDestroyed) return false;
    try {
      return Boolean(editor?.can?.()?.setHorizontalRule?.());
    } catch (e) {
      return false;
    }
  })();

  const canInsertPageBreak = (() => {
    if (!editor || editor?.isDestroyed) return false;
    try {
      return Boolean(editor?.can?.()?.setPageBreak?.());
    } catch (e) {
      return false;
    }
  })();

  // Safely resolve editor.can() once per render to avoid throwing when editor is destroyed or during teardown. Use this for tooltip checks below.
  const editorCan = (() => {
    if (!editor || editor?.isDestroyed) return null;
    try {
      return editor?.can?.();
    } catch (e) {
      return null;
    }
  })();

  // Needs to be memoized to prevent re-renders because it is used as init state.
  const toolbarButtons = useCallback(() => {
    const _buttons: TToolbarButton[] = [
      {
        key: 'undo',
        title: t('blox.editorial.toolbar.undo'),
        ariaLabel: t('blox.editorial.toolbar.undo_change'),
        onClick: actions.toggleUndo,
        tooltipTitle: !editorCan?.undo?.() ? '' : t('blox.editorial.toolbar.undo'),
        disabled: !isUndoEnabled,
        icon: UndoIcon,
      },
      {
        key: 'redo',
        title: t('blox.editorial.toolbar.redo'),
        ariaLabel: t('blox.editorial.toolbar.redo_change'),
        onClick: actions.toggleRedo,
        tooltipTitle: !editorCan?.redo?.() ? '' : t('blox.editorial.toolbar.redo'),
        disabled: !isRedoEnabled,
        icon: RedoIcon,
      },
      {
        key: 'searchReplace',
        title: t('blox.editorial.toolbar.search_replace.tooltip'),
        ariaLabel: t('blox.editorial.toolbar.search_replace.tooltip'),
        onClick: () => setSearchReplaceOpen((prev: boolean) => !prev),
        tooltipTitle: !hasSearchReplaceExt
          ? ''
          : t('blox.editorial.toolbar.search_replace.tooltip'),
        disabled: !hasSearchReplaceExt,
        className: classNames({
          'Mui-selected': searchReplaceOpen,
        }),
        icon: FindReplaceIcon,
      },
      {
        key: 'heading',
        component: HeadingSelect,
        ariaLabel: t('blox.editorial.toolbar.heading_level'),
        disabled: !hasHeadingExt,
      },
      {
        key: 'bold',
        title: t('blox.editorial.toolbar.bold'),
        onClick: actions.toggleBold,
        tooltipTitle: t('blox.editorial.toolbar.bold'),
        icon: FormatBoldIcon,
        disabled: !hasBoldExt,
        className: classNames({ 'Mui-selected': !disabled && isActiveBold }),
      },
      {
        key: 'italic',
        title: t('blox.editorial.toolbar.italic'),
        onClick: actions.toggleItalic,
        tooltipTitle: t('blox.editorial.toolbar.italic'),
        icon: FormatItalicIcon,
        disabled: !hasItalicExt,
        className: classNames({ 'Mui-selected': !disabled && isActiveItalic }),
      },
      {
        key: 'underline',
        title: t('blox.editorial.toolbar.underline'),
        onClick: actions.toggleUnderline,
        tooltipTitle: t('blox.editorial.toolbar.underline'),
        icon: FormatUnderlinedIcon,
        disabled: !hasUnderlineExt,
        className: classNames({ 'Mui-selected': !disabled && isActiveUnderline }),
      },
      {
        key: 'link',
        component: LinkSelect,
        disabled: !hasLinkExt,
        className: classNames({ 'Mui-selected': !disabled && isActiveLink }),
      },
      {
        key: 'textAlign',
        component: TextAlignSelect,
        disabled: !hasTextAlignExt,
      },
      {
        key: 'bulletList',
        title: t('blox.editorial.toolbar.bullet_list'),
        ariaLabel: t('blox.editorial.toolbar.bullet_list_aria'),
        onClick: actions.toggleBulletList,
        tooltipTitle: t('blox.editorial.toolbar.bullet_list'),
        icon: FormatListBulletedIcon,
        disabled: !hasBulletListExt,
        className: classNames({ 'Mui-selected': !disabled && isActiveBulletList }),
      },
      {
        key: 'orderedList',
        title: t('blox.editorial.toolbar.numbered_list'),
        onClick: actions.toggleOrderedList,
        tooltipTitle: t('blox.editorial.toolbar.numbered_list'),
        icon: FormatListNumberedIcon,
        disabled: !hasOrderedListExt,
        className: classNames({ 'Mui-selected': !disabled && isActiveOrderedList }),
      },
      {
        key: 'horizontalRule',
        title: t('blox.editorial.toolbar.horizontal_rule'),
        ariaLabel: t('blox.editorial.toolbar.horizontal_rule_aria'),
        onClick: actions.insertHorizontalRule,
        tooltipTitle: t('blox.editorial.toolbar.horizontal_rule'),
        icon: HorizontalRuleIcon,
        disabled: !canInsertHorizRule,
        // Insert-only action: no active/selected state.
        // Also avoids cross-contamination with pageBreak which shares the same 'horizontalRule' extension name.
        className: '',
      },
      {
        key: 'pageBreak',
        title: t('blox.editorial.toolbar.page_break'),
        ariaLabel: t('blox.editorial.toolbar.page_break_aria'),
        onClick: actions.insertPageBreak,
        tooltipTitle: t('blox.editorial.toolbar.page_break'),
        icon: InsertPageBreakIcon,
        disabled: !canInsertPageBreak,
        // Insert-only action: no active/selected state.
        className: '',
      },
      {
        key: 'inlineNote',
        title: t('blox.editorial.toolbar.inline_note'),
        onClick: actions.toggleInlineNote,
        tooltipTitle: (
          <>
            <Box>{t('blox.editorial.toolbar.inline_note')}</Box>
            <Box>{t('blox.editorial.toolbar.omitted_from_published')}</Box>
          </>
        ),
        icon: EditNoteIcon,
        className: classNames({
          'Mui-selected': !disabled && !isSelectionEmpty && isActiveInlineNote,
        }),
        disabled: !hasInlineNoteExt || isSelectionEmpty,
      },
      ...(hasPrint
        ? ([
            {
              key: 'printTrim',
              title: t('blox.editorial.toolbar.print_trim'),
              onClick: actions.togglePrintTrim,
              tooltipTitle: (
                <>
                  <Box>{t('blox.editorial.toolbar.trim_from_print')}</Box>
                  <Box>{t('blox.editorial.toolbar.omitted_from_print')}</Box>
                </>
              ),
              icon: PrintDisabledIcon,
              className: classNames({
                'Mui-selected': !disabled && !isSelectionEmpty && isActivePrintTrim,
              }),
              disabled: !hasPrintTrimExt || isSelectionEmpty,
            },
          ] as TToolbarButton[])
        : []),
      {
        key: 'blockQuote',
        title: t('blox.editorial.toolbar.block_quote'),
        onClick: actions.toggleBlockQuote,
        tooltipTitle: t('blox.editorial.toolbar.block_quote'),
        icon: FormatQuoteIcon,
        disabled: !hasBlockQuoteExt,
        className: classNames({ 'Mui-selected': !disabled && isActiveBlockQuote }),
      },
      {
        key: 'table',
        title: t('blox.editorial.toolbar.table'),
        onClick: actions.insertTable,
        // Insert-only action: no active/selected state.
        className: '',
        tooltipTitle: t('blox.editorial.toolbar.table'),
        icon: TableChartOutlinedIcon,
        disabled: !hasTableExt,
      },
      {
        key: 'specialCharacters',
        component: SpecialCharacters,
      },
      {
        key: 'aiSelect',
        component: AISelect,
        disabled: !hasAiSelectExt && isSelectionEmpty,
        additionalCheck: isAIEnabled,
      },
      {
        key: 'widgetSelect',
        component: WidgetSelect,
      },
    ];
    // Insert the mobile menu popper.
    if (isMobileMenuEnabled) {
      _buttons.unshift({
        key: 'mobilePopper',
        title: t('blox.editorial.toolbar.more'),
        tooltipTitle: t('blox.editorial.toolbar.open_more_options'),
        icon: MoreVertIcon,
        className: 'mobile-popper',
        ariaDescribedby: popperId,
        disabled: false,
        onClick: (e: React.MouseEvent<HTMLElement>) => {
          setAnchorEl(!!anchorEl ? null : e.currentTarget);
        },
      });
    }
    // Return the buttons based on the buttons prop.
    if (!isAllButtons && Array.isArray(buttons) && !isArticleMainEditor) {
      return _buttons.filter((button) => buttons.includes(button.key));
    }
    // Default, return all buttons.
    return _buttons;
  }, [
    t,
    anchorEl,
    buttons,
    isAllButtons,
    isArticleMainEditor,
    disabled,
    isMobileMenuEnabled,
    isRedoEnabled,
    isUndoEnabled,
    actions,
    isSelectionEmpty,
    hasHeadingExt,
    hasBoldExt,
    hasItalicExt,
    hasUnderlineExt,
    hasLinkExt,
    hasTextAlignExt,
    hasBulletListExt,
    hasOrderedListExt,
    hasBlockQuoteExt,
    canInsertHorizRule,
    canInsertPageBreak,
    hasInlineNoteExt,
    hasPrintTrimExt,
    hasTableExt,
    hasAiSelectExt,
    hasSearchReplaceExt,
    isAIEnabled,
    hasPrint,
    searchReplaceOpen,
    isActiveBold,
    isActiveItalic,
    isActiveUnderline,
    isActiveLink,
    isActiveBulletList,
    isActiveOrderedList,
    isActiveBlockQuote,
    isActiveInlineNote,
    isActivePrintTrim,
    editorCan,
  ]);

  // State for the main menu and mobile menu buttons.
  const [mainButtonsKeys, setMainButtonsKeys] = useState<string[]>(
    toolbarButtons().map((button) => button.key),
  );
  const [mobileButtonsKeys, setMobileButtonsKeys] = useState<string[]>([]);

  const updateToolbarButtons = useCallback(() => {
    const width = Math.round(elementWidth);
    const allButtonKeys = toolbarButtons()?.map((button) => button.key);

    // Check for width.
    if (isMobileMenuEnabled && width && width !== 0) {
      let mobileMenuButtonKeyFilterList: string[] = [];
      // Filter list based on width - buttons to hide at each breakpoint.
      // Prioritizes frequently-used buttons visible longer.
      const filters = {
        lg: [
          'table',
          'blockQuote',
          'horizontalRule',
          'pageBreak',
          'inlineNote',
          'printTrim',
          'specialCharacters',
        ],
        md: ['textAlign', 'bulletList', 'orderedList'],
        sm: ['bold', 'italic', 'underline', 'link'],
      };
      if (width >= breakPoints.xl) {
        mobileMenuButtonKeyFilterList = [];
        if (buttons?.length && !isAllButtons && !isArticleMainEditor) {
          mobileMenuButtonKeyFilterList = allButtonKeys.filter(
            (button) => !buttons.includes(button),
          );
        }
      }
      // Desktop view.
      if (width < breakPoints.xl) {
        mobileMenuButtonKeyFilterList = [
          ...new Set([...mobileMenuButtonKeyFilterList, ...filters['lg']]),
        ];
      }

      // Tablet view.
      if (width <= breakPoints.lg) {
        mobileMenuButtonKeyFilterList = [
          ...new Set([
            ...mobileMenuButtonKeyFilterList,
            ...filters['lg'],
            ...filters['md'],
          ]),
        ];
      }
      // Small view.
      if (width <= breakPoints.md) {
        mobileMenuButtonKeyFilterList = [
          ...new Set([
            ...mobileMenuButtonKeyFilterList,
            ...filters['lg'],
            ...filters['md'],
            ...filters['sm'],
          ]),
        ];
      }
      // Mobile view.
      if (width <= breakPoints.sm) {
        mobileMenuButtonKeyFilterList = allButtonKeys?.filter(
          (button) =>
            button !== 'mobilePopper' &&
            button !== 'widgetSelect' &&
            button !== 'aiSelect',
        );
      }

      const mobileMenuButtonsKeys = allButtonKeys.filter((key) => {
        return mobileMenuButtonKeyFilterList.includes(key);
      });
      const mainMenuButtonsKeys = allButtonKeys.filter((key) => {
        if (width >= breakPoints.xl && key === 'mobilePopper') {
          return false;
        } else {
          return !mobileMenuButtonKeyFilterList.includes(key);
        }
      });
      // Set the keys to filter.
      setMobileButtonsKeys(mobileMenuButtonsKeys);
      setMainButtonsKeys(mainMenuButtonsKeys);
    } else {
      setMainButtonsKeys(allButtonKeys);
    }
  }, [
    elementWidth,
    toolbarButtons,
    isMobileMenuEnabled,
    buttons,
    isAllButtons,
    isArticleMainEditor,
  ]);

  // Monitor Editor transactions to update the toolbar buttons.
  useEffect(() => {
    if (!editor) return;

    editor.on('transaction', updateToolbarButtons);

    return () => {
      editor.off('transaction', updateToolbarButtons);
    };
  }, [editor, updateToolbarButtons]);

  const debouncedUpdateToolbarButtons = useDebouncedCallback(updateToolbarButtons, {
    delay: 100, // Adjust delay as needed
    flushOnUnmount: true,
  });

  // Handle window resize events separately with debouncing
  useEffect(() => {
    window.addEventListener('resize', debouncedUpdateToolbarButtons);

    return () => {
      window.removeEventListener('resize', debouncedUpdateToolbarButtons);
    };
  }, [debouncedUpdateToolbarButtons]);

  useEffect(() => {
    if (!hasSearchReplaceExt) {
      setSearchReplaceOpen(false);
    }
  }, [hasSearchReplaceExt, editor]);

  // Add a keyboard event handler to detect Ctrl+F
  useEffect(() => {
    if (!editor || !hasSearchReplaceExt) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if focus is in the editor when Ctrl+F or Cmd+F is pressed
      if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        /**
         * Only prevent default and open search/replace if this is
         * the article main editor, otherwise allow browser default
         */
        if (isArticleMainEditor) {
          event.preventDefault(); // Prevent the browser's default search
          setSearchReplaceOpen((prev) => !prev); // Toggle the search/replace menu
        }
        // Otherwise, let the browser handle the Ctrl+F normally
      }
    };

    // Add the event listener to the document
    document.addEventListener('keydown', handleKeyDown);

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editor, hasSearchReplaceExt, isArticleMainEditor]);

  // Ref to persist the latest updateToolbarButtons function
  const updateToolbarButtonsRef = useRef(updateToolbarButtons);
  updateToolbarButtonsRef.current = updateToolbarButtons;

  // Update the toolbar buttons when dependencies change
  useEffect(() => {
    updateToolbarButtonsRef.current();
  }, [elementWidth, isMobileMenuEnabled, buttons, isAllButtons, isArticleMainEditor]);

  return (
    <Box
      sx={{
        position: 'relative',
        zIndex: !disabled ? 1100 : 800,
        ...(sx ? (typeof sx === 'function' ? sx(theme) : sx) : {}),
      }}
    >
      <Toolbar
        id={id}
        sx={{
          ...(sx ? (typeof sx === 'function' ? sx(theme) : sx) : {}),
          // Hide the mobile menu when the screen is large--according to breakpoints.
          containerType: 'inline-size',
          [`@container (min-width: ${breakPoints.xl}px)`]: {
            '& .mobile-popper': { display: 'none' },
          },
        }}
      >
        <ToggleButtonGroup
          ref={ref}
          id="toolbar-content"
          data-testid="editor-toolbar"
          className="toolbar"
          size="small"
          sx={{
            // Prevent MUI's grouped-button border/margin rules from shifting layout
            '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
              marginLeft: 0,
              borderLeft: 'none',
            },
            '& .MuiToggleButtonGroup-grouped.Mui-selected:not(:first-of-type)': {
              marginLeft: 0,
              borderLeft: 'none',
            },
          }}
        >
          <EditorBarButtons
            buttons={toolbarButtons().filter((button) =>
              mainButtonsKeys.includes(button.key),
            )}
            enabledButtons={buttons}
            isAllButtons={isAllButtons}
            tooltipPlacement="bottom"
            disabled={disabled}
            editor={editor}
            articleMainContent={articleMainContent}
          />
        </ToggleButtonGroup>

        {/* Mobile Menu. */}
        <Popper
          id={popperId}
          data-testid={popperId}
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="bottom-start"
          sx={{ zIndex: 1100 }}
        >
          <Paper>
            <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
              <ToggleButtonGroup
                id="mobile-toolbar-content"
                className="toolbar"
                size="small"
                sx={(theme) => {
                  const isLightTheme: boolean = theme.palette.mode === 'light';
                  const textColor = isLightTheme
                    ? theme.palette.text.primary
                    : theme.palette.text.secondary;
                  return {
                    // Rendering the buttons as compactly as possible.
                    display: 'flex',
                    maxWidth: 400,
                    flexFlow: 'wrap',
                    // Reset toolbar button styles.
                    '& .toolbar-button': {
                      border: 'none',
                      color: textColor,
                    },
                    // Hide the mobile popper button.
                    '& .mobile-popper': { display: 'none' },
                    // Hide the widget select button.
                    '& #widget-select button': { display: 'none' },
                    // Hide the special characters button.
                    '& .specialCharacters': {
                      display: 'none',
                    },
                    // Hide all select dropdown buttons.
                    '& .select-dropdown button': {
                      color: textColor,
                    },
                  };
                }}
              >
                <EditorBarButtons
                  buttons={toolbarButtons().filter((button) => {
                    return mobileButtonsKeys.includes(button.key);
                  })}
                  enabledButtons={buttons}
                  isAllButtons={isAllButtons}
                  tooltipPlacement="bottom"
                  disabled={disabled}
                  editor={editor}
                  articleMainContent={articleMainContent}
                />
              </ToggleButtonGroup>
            </ClickAwayListener>
          </Paper>
        </Popper>
      </Toolbar>
      <SearchAndReplaceMenu
        editor={editor}
        open={searchReplaceOpen}
        onClose={() => {
          setSearchReplaceOpen(false);
          editor?.commands.clearSearch();
        }}
      />
      <EditorBubbleMenu editor={editor} />
    </Box>
  );
};
export default EditorBar;
